import React, { useContext } from 'react';
import { ContactContext } from '../../../../../context/ContactContext';
import {
  ContactMutations,
  DeleteContactResponse,
  DeleteContactVariables,
} from '../../../../../graphql/contact.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import { Contact, ContactInput } from '../../../../../types/contact';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Button, ButtonVariant, CancelButton } from '../../../../components/Button';
import { AlertModal } from '../../../../components/Modal';
import { ToastType } from '../../../../components/Toast';

interface DeleteContactModalProps {
  contact: Contact;
  open: boolean;
  setOpen: (open: boolean) => void;
  onDeleted?: () => void;
}

export default function DeleteContactModal({ contact, open, setOpen, onDeleted }: DeleteContactModalProps) {
  const { showToast } = useToast();

  const { contacts, setContacts } = useContext(ContactContext);

  const [remove, { loading }] = useMutation<DeleteContactResponse, DeleteContactVariables>(ContactMutations.remove, {
    variables: { contacts: [new ContactInput(contact).forDelete()] },
    onCompleted: response => {
      contacts.delete(response.deleteContact[0].id);
      setContacts(new Map(contacts));
      showToast({
        type: ToastType.success,
        title: `${t(TranslationKey.delete).singular.upper} ${t(TranslationKey.contact).singular.upper}`,
      });
      setOpen(false);
      onDeleted?.();
    },
  });

  return (
    <AlertModal
      title={`${t(TranslationKey.delete).singular.upper} ${t(TranslationKey.contact).singular.upper}`}
      open={open}
      setOpen={setOpen}
    >
      <div className='grid gap-4'>
        <p className='subtitle'>{`${t(TranslationKey.delete).singular.upper} ${
          t(TranslationKey.contact).singular.upper
        }?`}</p>
        <div className='flex justify-end gap-2'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <Button
            variant={ButtonVariant.danger}
            text={t(TranslationKey.delete).singular.upper}
            onClick={() => remove()}
            loading={loading}
          />
        </div>
      </div>
    </AlertModal>
  );
}
